import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type PropsWithChildren,
} from "react";
import type { JsonValue } from "@/api/json";
import { createPortal } from "react-dom";

export type ToastOptions = {
  durationMs?: number | null;
};

type ToastTone = "success" | "error";

type ToastEntry = {
  id: string;
  tone: ToastTone;
  message: string;
  durationMs: number | null;
};

type ToastContextValue = {
  success(message: string, options?: ToastOptions): string;
  error(reason: Error | JsonValue | undefined, options?: ToastOptions & { fallback?: string }): string;
  dismiss(id: string): void;
};

const ToastContext = createContext<ToastContextValue | null>(null);
const DEFAULT_SUCCESS_DURATION = 5_000;

function errorMessage(reason: Error | JsonValue | undefined, fallback: string) {
  if (reason instanceof Error && reason.message.trim()) return reason.message;
  if (typeof reason === "string" && reason.trim()) return reason;
  return fallback;
}

function ToastIcon({ tone }: { tone: ToastTone }) {
  return (
    <svg
      className="toast__icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {tone === "success" ? (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="m8 12 2.6 2.6L16.5 9" />
        </>
      ) : (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5v6M12 16.8v.1" />
        </>
      )}
    </svg>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastEntry; onDismiss(id: string): void }) {
  const remainingMs = useRef(toast.durationMs ?? 0);
  const startedAt = useRef(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;

  useEffect(() => {
    if (toast.durationMs === null || paused || remainingMs.current <= 0) return;
    startedAt.current = Date.now();
    const timer = window.setTimeout(() => onDismiss(toast.id), remainingMs.current);
    return () => {
      window.clearTimeout(timer);
      remainingMs.current = Math.max(0, remainingMs.current - (Date.now() - startedAt.current));
    };
  }, [onDismiss, paused, toast.durationMs, toast.id]);

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
  };

  return (
    <article
      className={`toast toast--${toast.tone}`}
      role={toast.tone === "error" ? "alert" : "status"}
      aria-live={toast.tone === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
    >
      <ToastIcon tone={toast.tone} />
      <div className="toast__copy">
        <strong>{toast.tone === "success" ? "Operación completada" : "No se pudo completar"}</strong>
        <p>{toast.message}</p>
      </div>
      <button type="button" className="toast__close" onClick={() => onDismiss(toast.id)} aria-label="Cerrar notificación">
        <svg viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="m6 6 8 8M14 6l-8 8" />
        </svg>
      </button>
    </article>
  );
}

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const add = useCallback((tone: ToastTone, message: string, durationMs: number | null) => {
    const id = `toast-${Date.now()}-${nextId.current++}`;
    setToasts((current) => [{ id, tone, message, durationMs }, ...current]);
    return id;
  }, []);

  const success = useCallback(
    (message: string, options?: ToastOptions) =>
      add("success", message, options?.durationMs === undefined ? DEFAULT_SUCCESS_DURATION : options.durationMs),
    [add],
  );

  const error = useCallback(
    (reason: Error | JsonValue | undefined, options?: ToastOptions & { fallback?: string }) =>
      add(
        "error",
        errorMessage(reason, options?.fallback ?? "No se pudo completar la operación."),
        options?.durationMs ?? null,
      ),
    [add],
  );

  const value = useMemo(() => ({ success, error, dismiss }), [dismiss, error, success]);
  const stack = (
    <section className="toast-stack" aria-label="Notificaciones">
      {toasts.map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />)}
    </section>
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document === "undefined" ? null : createPortal(stack, document.body)}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("ToastProvider no está configurado.");
  return context;
}
